import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ImageFileUpload from "./ImageFileUpload";
import {toYearMonthDay} from "../pages/utils";
import SymptomSeverities from "./SymptomSeverities";
import SymptomImages from "./SymptomImages";

const Symptom = ({ symptomParams, category, onUpdateSymptom, onDeleteImage, onAddImages, onSaveNewSymptoms }) => {
  const { t } = useTranslation();
  const todayYmd = useMemo(() => toYearMonthDay(new Date()), []);
  const [symptoms, setSymptoms] = useState([]);

  const getReportingUrl = () => {
    const language = (typeof navigator !== "undefined" && navigator.language) ? navigator.language : "en";

    const langMap = {
      de: "https://nebenwirkungen.bund.de/SiteGlobals/Forms/nebenwirkungen/01-person/person-node.html",
      sl: "https://www.jazmp.si/humana-zdravila/farmakovigilanca/porocanje-o-nezelenih-ucinkih-zdravil/",
      nl: "https://meldformulier.lareb.nl/Forms/reportform",
      el: "https://www.eof.gr/web/guest",
      en: "https://www.adrreports.eu/en/report_side_effect.html",
    };

    const langCode = language.split("-")[0];
    return langMap[langCode] || langMap.en;
  };

  useEffect(() => {
    if (Array.isArray(symptomParams) && symptomParams.length > 0) {
      const initSymptoms = symptomParams.map((param, i) => ({
        id: param?.id ?? -1,
        type: param?.type ?? category ?? "",
        term: param?.term ?? "",
        sufferingLevel: typeof param?.sufferingLevel === "number" ? param.sufferingLevel : undefined,
        comment: param?.comment ?? "",
        number: i,
        startDate: param?.startDate ? toYearMonthDay(param.startDate) : todayYmd,
        endDate: param?.endDate ? toYearMonthDay(param.endDate) : "",
        images: param?.images ?? [],
      }));

      setSymptoms(initSymptoms);
    } else {
      setSymptoms([]);
    }
  }, [symptomParams, category, todayYmd]);

  const handleSelectedSufferingLevel = (sufferingLevel, ind) => {
    setSymptoms((prev) =>
      prev.map((symptom, i) => (i === ind ? { ...symptom, sufferingLevel } : symptom))
    );
  };

  const handleStartDates = (value, ind) => {
    setSymptoms((prev) =>
      prev.map((symptom, i) => (i === ind ? { ...symptom, startDate: value } : symptom))
    );
  };

  const handleEndDates = (value, ind) => {
    setSymptoms((prev) =>
      prev.map((symptom, i) =>
        i === ind ? { ...symptom, endDate: value || "", deactivated: !!value } : symptom
      )
    );
  };

  const updateComments = (value, ind) => {
    setSymptoms((prev) =>
      prev.map((symptom, i) => (i === ind ? { ...symptom, comment: value } : symptom))
    );
  };

  const handleFilesChanged = (symptomNumber, files) => {
    setSymptoms((prev) =>
      prev.map((symptom, i) => (i === symptomNumber ? { ...symptom, files } : symptom))
    );
  };

  const handleSubmit = (cancel) => {
    const payload = cancel === true ? null : symptoms;

    if (category === null || !onSaveNewSymptoms) {
      onUpdateSymptom?.(payload);
    } else {
      onSaveNewSymptoms?.(payload);
    }
  };

  const checkRequiredFields = () =>
    symptoms.length > 0 && symptoms.every((symptom) => typeof symptom?.sufferingLevel === "number");

  const group = (category ?? (Array.isArray(symptomParams) && symptomParams[0]?.type)) ?? "";

  return (
    <>
      <header className="w3-container w3-padding-small w3-center">
        {group ? <p>{t(group)}</p> : null}
        <label>{t("please_select_the_severity_of_the_symptoms")}</label>
      </header>
      <div className="w3-container form-content-scroll">
        {symptoms.map((symptom, i) => (
          <div key={`symptom-${i}`} className="w3-margin">
            <header className="w3-theme-d5 w3-padding w3-margin-bottom">
              <label className="info-panel-text">{t(symptom?.term)}</label>
            </header>
            <div className="w3-container w3-border">
              <SymptomSeverities
                category={category}
                symptom={symptom}
                symptomNumber={i}
                handleSelectedSufferingLevel={handleSelectedSufferingLevel}
              />
              <div className="w3-row w3-padding-small">
                <div className="w3-quarter">
                  <label>{t("start_date")}*</label>
                  <input
                    type="date"
                    value={symptom?.startDate || todayYmd}
                    className="w3-input w3-border w3-round form-input"
                    onChange={(e) => handleStartDates(e.target.value, i)}
                  />
                </div>
                <div className="w3-quarter">
                  <label>{t("end_date")}</label>
                  <input
                    type="date"
                    value={symptom?.endDate || ""}
                    className="w3-input w3-border w3-round form-input"
                    onChange={(e) => handleEndDates(e.target.value, i)}
                  />
                </div>
                <div className="w3-half w3-padding-small">
                  <label>{t("comment")}:</label>
                  <textarea
                    className="form-input"
                    value={symptom?.comment || ""}
                    onChange={(e) => updateComments(e.target.value, i)}
                  />
                </div>
              </div>
              <div className="w3-row w3-padding-small">
                <div className="w3-col s7 w3-mobile">
                { category === null
                  ?
                  <SymptomImages symptom={symptom} onDeleteImage={onDeleteImage} onAddImages={onAddImages} />
                  :
                  <ImageFileUpload symptomNumber={i} onFilesChanged={handleFilesChanged} />
                }
                </div>
                <div className="w3-col s5 w3-mobile w3-padding-small w3-right-align">
                  <a
                    href={getReportingUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <label className="label-link">
                      {t("report_to_national_authorities")}
                    </label>
                  </a>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <footer className="w3-container">
        <div className="w3-right">
          <button
            onClick={() => handleSubmit(false)}
            type="button"
            className="w3-btn w3-round-xlarge w3-theme-d5 w3-margin"
            disabled={!checkRequiredFields()}
          >
            {t("submit")}
          </button>
          <button
            onClick={() => handleSubmit(true)}
            className="w3-btn w3-round-xlarge w3-theme-d5 w3-margin"
            type="button"
          >
            {t("cancel")}
          </button>
        </div>
      </footer>
    </>
  );
};

export default Symptom;
